import { DestroyRef, inject, Injectable, signal, type Signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Apollo } from 'apollo-angular';
import type { ManualLocation } from './manual-help.service';
import {
  manualReportSearchNoClickMutation,
  manualSearchQuery,
  type ManualSearchResult,
} from './manual.graphql';

interface SearchedQuery {
  lang: string;
  query: string;
}

/**
 * Search state of one host (the help panel or the `/manual` page, each provides its own instance
 * so a search typed in one never shows in the other) and the "no click" signal. A search that returned results stays
 * `pending` until the user clicks a result; if the next search or the panel closing comes first,
 * the backend is told so it can log the failed search. Searches with no results are logged by the
 * backend itself and never reported from here.
 */
@Injectable()
export class ManualSearchService {
  private readonly apollo = inject(Apollo);
  private readonly transloco = inject(TranslocoService);
  private readonly _results = signal<ManualSearchResult[] | null>(null);
  private readonly _searching = signal(false);
  private pending: SearchedQuery | null = null;
  private latestSearch = 0;

  constructor() {
    // Leaving the host (the panel unmounts, the user leaves /manual) with results and no click.
    inject(DestroyRef).onDestroy(() => this.reset());
  }

  /** Results of the last search; `null` when none is on screen (never searched, or one was opened). */
  readonly results: Signal<ManualSearchResult[] | null> = this._results.asReadonly();

  readonly searching: Signal<boolean> = this._searching.asReadonly();

  search(rawQuery: string): void {
    const query = rawQuery.trim();
    if (!query) return;

    this.reportPending();
    const lang = this.transloco.getActiveLang();
    const search = ++this.latestSearch;
    this._searching.set(true);
    this._results.set(null);

    // network-only: the backend logs a no-result search, so a cached answer would hide it.
    this.apollo
      .query({ query: manualSearchQuery, variables: { lang, query }, fetchPolicy: 'network-only' })
      .subscribe({
        next: (result) => {
          if (search !== this.latestSearch) return;
          const found = result.error ? [] : (result.data?.manualSearch ?? []);
          this._results.set(found);
          this._searching.set(false);
          if (found.length > 0) this.pending = { lang, query };
        },
        error: () => {
          if (search !== this.latestSearch) return;
          this._results.set([]);
          this._searching.set(false);
        },
      });
  }

  /**
   * A click on a result: it makes the search count as answered, hides the results and returns the
   * location of the result (its chapter and section id). The host decides what opening means.
   */
  open(result: ManualSearchResult): ManualLocation {
    this.pending = null;
    this._results.set(null);
    return { chapterId: result.chapterId, sectionId: result.id };
  }

  /** The panel closed: report a search left without click and forget the search. */
  reset(): void {
    this.reportPending();
    this.latestSearch++;
    this._searching.set(false);
    this._results.set(null);
  }

  private reportPending(): void {
    const searched = this.pending;
    if (!searched) return;
    this.pending = null;
    // Fire and forget: the global error link reports a failure and the signal is only telemetry.
    this.apollo
      .mutate({ mutation: manualReportSearchNoClickMutation, variables: searched })
      .subscribe({ error: () => undefined });
  }
}
